// censusClient -- REST client for the work-census read surface: which
// initiatives (bodies of work derived from the content-fingerprint index) a
// session was part of, and which initiatives travel together.
//
// Wire conventions:
// - Responses are protojson: camelCase field names, zero values emitted.
// - Origin UUIDs travel as base64 protobuf bytes. This module is a SANCTIONED
//   hyphenated-UUID conversion boundary (see dashboard/AGENTS.md "IDs on the
//   wire"): analytics surfaces identify sessions by hyphenated uuid, and the
//   census routes take the same ids as protobuf bytes, so the conversion
//   happens here and the helper stays local.
// - `rebuildId` (the generation echo) is an opaque base64 string; pass it
//   back verbatim to pin follow-up reads, never decode it.

import { idForPath, restGet, restPost } from "./restClient.js";

/// Hyphenated uuid -> standard base64 of its 16 raw bytes (protobuf `bytes`
/// over the JSON gateway). Returns null for anything that is not a uuid.
function uuidToBytesBase64(uuid) {
    const hex = String(uuid || "").replace(/-/g, "").toLowerCase();
    if (!/^[0-9a-f]{32}$/.test(hex)) return null;
    let bin = "";
    for (let i = 0; i < 32; i += 2) {
        bin += String.fromCharCode(parseInt(hex.slice(i, i + 2), 16));
    }
    return btoa(bin);
}

/// Initiatives the session was part of, ranked by how strongly it
/// participates. Resolves `{ initiatives, rebuildId }`; an unknown session or
/// a tenant with no completed census reads as empty, never an error. Pass a
/// `rebuildId` echoed by an earlier read to pin follow-up lookups to the
/// same generation.
export async function listInitiativesForTrace(sessionUuid, { pageSize, rebuildId } = {}) {
    const id = uuidToBytesBase64(sessionUuid);
    if (!id) return { initiatives: [], rebuildId: "" };
    const res = await restGet(`/v1/census/traces/${idForPath(id)}/initiatives`, {
        page_size: pageSize,
        rebuild_id: rebuildId,
    });
    return {
        initiatives: res?.initiatives ?? [],
        rebuildId: res?.rebuildId ?? "",
    };
}

/// Initiatives sharing sessions with `subject`, ranked by overlap. Pass the
/// `rebuildId` echoed by an earlier read so the neighborhood comes from the
/// same generation the rows on screen came from.
export async function listRelatedInitiatives(subject, { rebuildId, pageSize } = {}) {
    const res = await restGet("/v1/census/initiatives/related", {
        subject,
        rebuild_id: rebuildId,
        page_size: pageSize,
    });
    return {
        related: res?.related ?? [],
        rebuildId: res?.rebuildId ?? "",
    };
}

/// The tenant-wide initiative rollup for one generation, highest evidence
/// first -- the reference frame a per-session read is ranked against. Pass
/// the `rebuildId` echoed by the read being oriented. Resolves
/// `{ initiatives, rebuildId }`.
export async function listCensusInitiatives({ rebuildId, pageSize } = {}) {
    const res = await restGet("/v1/census/initiatives", {
        rebuild_id: rebuildId,
        page_size: pageSize,
    });
    return {
        initiatives: res?.initiatives ?? [],
        rebuildId: res?.rebuildId ?? "",
    };
}

/// Base64 protobuf bytes (a body's carrier) -> hyphenated session uuid, the
/// id analytics surfaces use. Returns null for anything that is not 16
/// bytes. The inverse of `uuidToBytesBase64`, kept in this boundary.
export function carrierToSessionId(b64) {
    let bin;
    try {
        bin = atob(String(b64 || ""));
    } catch {
        return null;
    }
    if (bin.length !== 16) return null;
    let hex = "";
    for (let i = 0; i < 16; i += 1) {
        hex += bin.charCodeAt(i).toString(16).padStart(2, "0");
    }
    return [
        hex.slice(0, 8),
        hex.slice(8, 12),
        hex.slice(12, 16),
        hex.slice(16, 20),
        hex.slice(20),
    ].join("-");
}

/// The evidence grain under one subject: its bodies, highest evidence first.
/// Pass the `rebuildId` echoed by the read that named the subject. Resolves
/// `{ bodies, rebuildId }`.
export async function listInitiativeBodies(subject, { rebuildId, pageSize } = {}) {
    const res = await restGet("/v1/census/bodies", {
        subject,
        rebuild_id: rebuildId,
        page_size: pageSize,
    });
    return {
        bodies: res?.bodies ?? [],
        rebuildId: res?.rebuildId ?? "",
    };
}

/// The tenant's census readings (generations), newest first, plus the pinned
/// (latest completed) one that default reads serve. Resolves
/// `{ generations, pinnedRebuildId }`.
export async function listCensusGenerations({ pageSize } = {}) {
    const res = await restGet("/v1/census/generations", { page_size: pageSize });
    return {
        generations: res?.generations ?? [],
        pinnedRebuildId: res?.pinnedRebuildId ?? "",
    };
}

/// Start a new census reading. Resolves `{ rebuildId, status }` immediately
/// ("running"); the reading appears in listCensusGenerations and completes
/// out-of-band. A trigger while another reading runs anywhere in the fleet
/// rejects with FAILED_PRECONDITION -- surface the server message and let the
/// user retry.
export async function triggerCensusSweep({ windowDays } = {}) {
    const res = await restPost("/v1/census/generations:sweep", {
        windowDays: windowDays || 0,
    });
    return {
        rebuildId: res?.rebuildId ?? "",
        status: res?.status ?? "",
    };
}

export const testables = { uuidToBytesBase64 };

/// The work register (docs/projects/work-register.md): the tenant's register
/// builds, newest first. A build's `buildId` is an opaque base64 string; pass
/// it back verbatim to getRegisterBuild and serveRegisterBuild.
export async function listRegisterBuilds({ pageSize } = {}) {
    const res = await restGet("/v1/census/register-builds", { page_size: pageSize });
    return { builds: (res?.builds ?? []).map(adaptRegisterBuild) };
}

/// Start a register build from the newest completed census reading. Resolves
/// `{ buildId, status }` ("queued"); the build appears in listRegisterBuilds
/// and completes out of band on a GPU node of its own. A trigger while a build
/// is queued or running for the tenant, or with no completed reading, rejects
/// with FAILED_PRECONDITION -- surface the server message.
export async function triggerRegisterBuild({ readBudget } = {}) {
    const res = await restPost("/v1/census/register-builds:trigger", {
        readBudget: readBudget || 0,
    });
    return { buildId: res?.buildId ?? "", status: res?.status ?? "" };
}

/// One build with the documents it wrote. Each document's `document` is the
/// register pipeline's JSON, parsed; the org's "profile" document is what
/// Serve sends to the judge.
export async function getRegisterBuild(buildId) {
    const res = await restGet(`/v1/census/register-builds/${idForPath(buildId)}`);
    return {
        build: res?.build ? adaptRegisterBuild(res.build) : null,
        documents: (res?.documents ?? []).map(adaptRegisterDocument),
    };
}

function adaptRegisterBuild(wire) {
    return {
        buildId: wire?.buildId ?? "",
        status: wire?.status ?? "",
        reason: wire?.reason ?? "",
        generationId: wire?.generationId ?? "",
        readBudget: Number(wire?.readBudget ?? 0),
        windowsRead: Number(wire?.windowsRead ?? 0),
        nodes: Number(wire?.nodes ?? 0),
        norms: Number(wire?.norms ?? 0),
        intents: Number(wire?.intents ?? 0),
        model: wire?.model ?? "",
        promptTokens: Number(wire?.promptTokens ?? 0),
        completionTokens: Number(wire?.completionTokens ?? 0),
        orgTokensSource: wire?.orgTokensSource ?? "",
        errorMessage: wire?.errorMessage ?? "",
        startedAt: wire?.startedAt ?? null,
        completedAt: wire?.completedAt ?? null,
        // When a completed build's documents are deleted (the server keeps them 90 days); null otherwise.
        documentsExpireAt: wire?.documentsExpireAt ?? null,
    };
}

function adaptRegisterDocument(wire) {
    let document = null;
    try {
        document = wire?.documentJson ? JSON.parse(wire.documentJson) : null;
    } catch {
        document = null;
    }
    return {
        node: wire?.node ?? "",
        kind: wire?.kind ?? "",
        depth: Number(wire?.depth ?? 0),
        norms: Number(wire?.norms ?? 0),
        intents: Number(wire?.intents ?? 0),
        document,
    };
}
